import React, { useState, useEffect } from 'react';
import {
  Select,
  MenuItem,
  FormControl,
  Box,
  Typography,
  Chip
} from '@mui/material';
import api from '../../api/axios';

/**
 * Model Selector Component
 * Allows switching AI models mid-conversation
 */
const ModelSelector = ({ sessionId, currentModel, onModelChange, projectId }) => {
  const [availableModels, setAvailableModels] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (projectId) {
      loadAvailableModels();
    }
  }, [projectId]);

  const loadAvailableModels = async () => {
    try {
      // Pass projectId to check project-specific API keys
      const response = await api.get(`/ai-flow-builder/available-models?projectId=${projectId}`);
      setAvailableModels(response.data.models || []);
    } catch (error) {
      console.error('Error loading models:', error);
      // Fallback to default models
      setAvailableModels([
        { id: 'claude-fable-5', name: 'Claude Fable 5', provider: 'Anthropic' },
        { id: 'claude-opus-5', name: 'Claude Opus 5', provider: 'Anthropic' },
        { id: 'claude-sonnet-5', name: 'Claude Sonnet 5', provider: 'Anthropic' },
        { id: 'claude-opus-4.8', name: 'Claude Opus 4.8', provider: 'Anthropic' },
        { id: 'claude-sonnet-4.6', name: 'Claude Sonnet 4.6', provider: 'Anthropic' },
        { id: 'claude-opus-4.5', name: 'Claude Opus 4.5', provider: 'Anthropic' },
        { id: 'claude-sonnet-4.5', name: 'Claude Sonnet 4.5', provider: 'Anthropic' },
        { id: 'gpt-5.6', name: 'GPT-5.6', provider: 'OpenAI' },
        { id: 'gpt-4o', name: 'GPT-4o', provider: 'OpenAI' },
        { id: 'gemini-3.7-flash', name: 'Gemini 3.7 Flash', provider: 'Google' },
        { id: 'gemini-3.6-flash', name: 'Gemini 3.6 Flash', provider: 'Google' },
        { id: 'gemini-3.5-flash', name: 'Gemini 3.5 Flash', provider: 'Google' },
        { id: 'gemini-3.5-flash-lite', name: 'Gemini 3.5 Flash-Lite', provider: 'Google' },
        { id: 'gemini-3.1-flash-lite', name: 'Gemini 3.1 Flash-Lite', provider: 'Google' },
        { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash', provider: 'Google' }
      ]);
    }
  };

  const handleModelChange = async (event) => {
    const newModel = event.target.value;
    
    if (newModel === currentModel) return;

    try {
      setLoading(true);
      await api.patch(`/ai-flow-builder/session/${sessionId}/model`, {
        model: newModel
      });
      onModelChange(newModel);
    } catch (error) {
      console.error('Error switching model:', error);
      alert('Failed to switch model. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getModelProvider = (modelId) => {
    const model = availableModels.find(m => m.id === modelId);
    return model?.provider || '';
  };

  return (
    <FormControl size="small" sx={{ minWidth: 200 }}>
      <Select
        value={currentModel}
        onChange={handleModelChange}
        disabled={loading}
        renderValue={(selected) => {
          const model = availableModels.find(m => m.id === selected);
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">
                {model?.name || selected}
              </Typography>
              <Chip
                label={model?.provider || ''}
                size="small"
                sx={{ height: 18, fontSize: '0.7rem' }}
              />
            </Box>
          );
        }}
      >
        {availableModels.map((model) => (
          <MenuItem key={model.id} value={model.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Typography variant="body2">{model.name}</Typography>
              <Chip
                label={model.provider}
                size="small"
                sx={{ ml: 2, height: 20, fontSize: '0.7rem' }}
              />
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default ModelSelector;

